import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from "../../Api/Api.json";

import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryBar,
} from 'victory';

// Icons for sleep metrics
const SleepDurationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const SleepScoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
    <polyline points="9 11 11 13 15 9" />
  </svg>
);

const SleepStartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10,8 16,12 10,16 10,8"></polygon>
  </svg>
);

const Sleep = () => {
  const navigate = useNavigate();
  const [hrvList, setHRVList] = useState([{}]);
  const [sleepDurationList, setSleepDurationList] = useState([{}]);
  const [sleepStartList, setSleepStartList] = useState([{}]);
  const [sleepEndList, setSleepEndList] = useState([{}]);
  const [sleepScoreList, setSleepScoreList] = useState([{}]);
  const [dayNameList, setDayNameList] = useState([]);
  const [status, setStatus] = useState('');

  // Transform data to use numeric x values based on array index
  const transformDataForChart = (dataList) => {
    if (!dataList || dataList.length === 0) return [];
    return dataList.map((item, index) => ({
      x: index + 1,  // Convert to 1-based numeric position
      y: item.y,     // Keep the y value (can be null)
      date: item.date,
      originalX: item.x  // Preserve original day name if needed
    }));
  };

  // Transformed data for charts
  const transformedSleepDurationList = transformDataForChart(sleepDurationList);
  const transformedSleepScoreList = transformDataForChart(sleepScoreList);
  const transformedSleepStartList = transformDataForChart(sleepStartList);

  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userid = JSON.parse(localStorage.getItem('user_info')).di_id;
      console.log(userid);

      const headers = { headers: { "Content-Type": "application/json" } }
      const url = Api.FITNESS_API;
      // Get user's actual timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //console.log(userTimezone);
      const requestData = {
        task: 'getSemionWellnessData',
        id: userid,
        timezone: userTimezone,
      };
      console.log('calling page data api');
      axios.post(url, requestData, headers)
        .then(result => {
          if (result.data) {
            console.log(JSON.stringify(result.data.sleepStartList));
            setHRVList(result.data.hrvList);
            setSleepDurationList(result.data.sleepDurationList);
            setSleepStartList(result.data.sleepStartList);
            setSleepEndList(result.data.sleepEndList);
            setDayNameList(result.data.dayNameList);
            setSleepScoreList(result.data.sleepScoreList);
          }
        });
    }
  }, []);

  // Generate random values for sleep metrics
  const generateRandomDuration = () => {
    return (Math.random() * 4 + 5).toFixed(1); // Random between 5-9 hours
  };

  const generateRandomScore = () => {
    return Math.floor(Math.random() * 26 + 70); // Random between 70-95
  };

  const generateRandomStart = () => {
    return (Math.random() * 3 + 9).toFixed(1); // Random between 9-12
  };

  const generateRandomPercentage = () => {
    const sign = Math.random() > 0.5 ? '+' : '-';
    const value = (Math.random() * 6).toFixed(1); // Random between 0-6%
    return `${sign}${value}%`;
  };

  const sleepMetrics = [
    {
      id: 'duration',
      name: 'Sleep Duration',
      description: 'Track your daily sleep duration and rest patterns',
      color: 'bg-[#58A6FF]',
      unit: 'hours',
      currentValue: sleepDurationList[6]?.y ? `${sleepDurationList[6].y}` : generateRandomDuration(),
      icon: <SleepDurationIcon />
    },
    {
      id: 'score',
      name: 'Sleep Score',
      description: 'Monitor your sleep quality and recovery metrics',
      color: 'bg-[#58A6FF]',
      unit: 'points',
      currentValue: sleepScoreList[6]?.y ? `${sleepScoreList[6].y}` : generateRandomScore(),
      icon: <SleepScoreIcon />
    },
    {
      id: 'start',
      name: 'Sleep Start',
      description: 'Track when you go to sleep each night',
      color: 'bg-[#58A6FF]',
      unit: 'time',
      currentValue: sleepStartList[6]?.y ? `${sleepStartList[6].y}:00` : `${generateRandomStart()}:00`,
      icon: <SleepStartIcon />
    }
  ];

  const handleCardClick = (metricType) => {
    // Handle card click - could open detailed view or measurement dialog
    console.log(`Clicked on ${metricType} metric`);
  };

  return (
    <div className="min-h-screen pt-14 pb-20">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium text-white">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Sleep Analysis</h1>
          <p className="text-white">Monitor your sleep patterns and quality</p>
        </div>



        {/* Sleep Metrics with Charts */}
        {sleepMetrics.map((metric) => (
          <div key={metric.id} className="mb-8">
            {/* Card */}
            <div
              onClick={() => handleCardClick(metric.id)}
              className="bg-[#161B22] rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group border border-[#30363D]"
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">Sleep Data</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-white text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>

                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-white">{metric.currentValue}</span>
                    <span className="text-white ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">Current Value</div>
                    <div className={`text-xs ${generateRandomPercentage().startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {generateRandomPercentage()}
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white">Average</span>
                      <div className="font-medium text-white">
                        {metric.id === 'duration' ? '7.5 hours' :
                         metric.id === 'score' ? '85 points' : '10:30 PM'}
                      </div>
                    </div>
                    <div>
                      <span className="text-white">Goal</span>
                      <div className="font-medium text-white">
                        {metric.id === 'duration' ? '8.0 hours' :
                         metric.id === 'score' ? '90 points' : '10:00 PM'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart placed immediately after its card */}
            {metric.id === 'duration' && (
              <div className="h-48 w-full overflow-hidden mt-4 px-[5px]">
                <VictoryChart
                  domain={{ y: [0, 12] }}
                  height={180}
                  padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                  style={{
                    parent: { width: '100%' }
                  }}
                >
                  <VictoryAxis
                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                    tickFormat={(t) => dayNameList[t - 1] || ''}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: null,
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickValues={[0, 2, 4, 6, 8, 10, 12]}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                    }}
                  />
                  <VictoryBar
                    data={transformedSleepDurationList.filter(d => d.y !== null)}
                    size={3}
                    style={{
                      data: { fill: '#3b82f6' },
                    }}
                  />
                  <VictoryLine
                    data={transformedSleepDurationList.filter(d => d.y !== null)}
                    interpolation="natural"
                    size={3}
                    style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                  />
                </VictoryChart>
              </div>
            )}

            {metric.id === 'score' && (
              <div className="h-48 w-full overflow-hidden mt-4 px-[5px]">
                <VictoryChart
                  domain={{ y: [50, 100] }}
                  height={180}
                  padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                  style={{
                    parent: { width: '100%' }
                  }}
                >
                  <VictoryAxis
                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                    tickFormat={(t) => dayNameList[t - 1] || ''}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: null,
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickValues={[60, 70, 80, 90, 100]}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                    }}
                  />
                  <VictoryBar
                    data={transformedSleepScoreList.filter(d => d.y !== null)}
                    size={3}
                    style={{
                      data: { fill: '#10b981' },
                    }}
                  />
                  <VictoryLine
                    data={transformedSleepScoreList.filter(d => d.y !== null)}
                    interpolation="natural"
                    size={3}
                    style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                  />
                </VictoryChart>
              </div>
            )}

            {metric.id === 'start' && (
              <div className="h-48 w-full overflow-hidden mt-4 px-[5px] mb-20">
                <VictoryChart
                  domain={{ y: [0, 12] }}
                  height={180}
                  padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                  style={{
                    parent: { width: '100%' }
                  }}
                >
                  <VictoryAxis
                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                    tickFormat={(t) => dayNameList[t - 1] || ''}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: null,
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickValues={[0, 2, 4, 6, 8, 10, 12]}
                    tickFormat={(t) => {
                      if (t === 0) return "12:00 AM";
                      if (t === 12) return "12:00 PM";
                      if (t > 12) return `${t - 12}:00 PM`;
                      return `${t}:00 AM`;
                    }}
                    style={{
                      axis: { stroke: '#8B949E' },
                      tickLabels: { fill: '#F0F6FC', fontSize: 23, padding: 3 },
                      grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' },
                    }}
                  />
                  <VictoryBar
                    data={transformedSleepStartList.filter(d => d.y !== null)}
                    size={3}
                    style={{
                      data: { fill: '#8b5cf6' },
                    }}
                  />
                  <VictoryLine
                    data={transformedSleepStartList.filter(d => d.y !== null)}
                    interpolation="natural"
                    size={3}
                    style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                  />
                </VictoryChart>
              </div>
            )}
          </div>
        ))}


      </div>
    </div>
  );
};

export default Sleep;